import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  PanelCard,
  Alert,
  Spinner,
  Switch,
  TextField,
  Chip,
  GLPGrid,
} from '@glplus/glpweb-dsl';
import { useVendor } from '../lib/CapabilitiesContext';
import { vendorApiClient, type VendorProfile } from '../lib/vendorApiClient';

/**
 * Store profile (Figma §5) — the public discovery-card editor. Business details
 * (green), Hours (navy), Eligibility & trust (orange), plus a live "public
 * preview" of the discovery card. Owner/manager (profile:edit) can edit; clerks
 * are read-only. A save writes a VendorAuditLog row server-side.
 */

const GREEN = '#25862D';
const NAVY = '#343F5B';
const ORANGE = '#F67A1A';

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

interface HourRow {
  label: string;
  value: string;
}
function readHours(v: unknown): HourRow[] {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return Object.entries(v as Record<string, unknown>).map(([label, value]) => ({
      label,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    }));
  }
  return [];
}

export default function StoreProfile() {
  const { t } = useTranslation();
  const { activeVendorId, has } = useVendor();
  const canEdit = has('profile:edit');

  const [profile, setProfile] = useState<VendorProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeVendorId) return;
    let alive = true;
    setProfile(null);
    setError(null);
    vendorApiClient
      .getProfile(activeVendorId)
      .then((p) => {
        if (!alive) return;
        setProfile(p);
      })
      .catch((e: Error) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [activeVendorId]);

  if (!activeVendorId || (profile === null && !error)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{t('profile.loadError', { error })}</Alert>;
  if (!profile) return null;

  const categories = asStringArray(profile.categories);
  const hours = readHours(profile.hours);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1100 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {t('profile.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.25 }}>
          {t('profile.subtitle', { defaultValue: 'This is your public discovery card on Grocery List Plus.' })}
        </Typography>
      </Box>

      {!canEdit && <Alert severity="info">{t('profile.viewOnly')}</Alert>}
      {canEdit && <Alert severity="info">{t('profile.reviewedIdentity')}</Alert>}

      <GLPGrid container spacing={3}>
        {/* Left column — editable cards */}
        <GLPGrid size={{ xs: 12, lg: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Business details */}
            <PanelCard accent={GREEN} title={t('profile.businessDetails')} sx={{ height: 'auto', bgcolor: '#F3F8F3' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={t('profile.businessName')}
                  ariaLabel={t('profile.businessName')}
                  value={profile.businessName}
                  onChange={() => undefined}
                  disabled
                />
                <TextField
                  label={t('profile.type', { defaultValue: 'Type' })}
                  ariaLabel={t('profile.type', { defaultValue: 'Type' })}
                  value={profile.businessType ?? ''}
                  onChange={() => undefined}
                  disabled
                />
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#3a4350', mb: 0.75 }}>
                    {t('profile.categories', { defaultValue: 'Categories' })}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {categories.length > 0 ? (
                      categories.map((c) => (
                        <Chip key={c} label={c} size="small" backgroundColor="#fff" textColor="#3a4350" fontSize={12} />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('profile.noCategories', { defaultValue: 'No categories yet.' })}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </PanelCard>

            {/* Hours */}
            <PanelCard accent={NAVY} title={t('profile.hours', { defaultValue: 'Hours' })} sx={{ height: 'auto', bgcolor: '#EEF1F8' }}>
              {hours.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {hours.map((h) => (
                    <Box key={h.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 600 }}>{h.label}</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#1f2733' }}>{h.value}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('profile.noHours', { defaultValue: 'Store hours not set yet.' })}
                </Typography>
              )}
            </PanelCard>

            {/* Eligibility & trust */}
            <PanelCard accent={ORANGE} title={t('profile.eligibility', { defaultValue: 'Eligibility & trust' })} sx={{ height: 'auto', bgcolor: '#FFF6EE' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>{t('profile.acceptsSnap')}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('profile.snapHelp', { defaultValue: 'Shown as a badge on your discovery card' })}
                    </Typography>
                  </Box>
                  <Switch
                    ariaLabel={t('profile.acceptsSnap')}
                    checked={profile.snapAuthorized}
                    onChange={() => undefined}
                    disabled
                    color="success"
                  />
                </Box>
              </Box>
            </PanelCard>

          </Box>
        </GLPGrid>

        {/* Right column — public preview */}
        <GLPGrid size={{ xs: 12, lg: 4 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#8a93a3', mb: 1.25 }}>
            {t('profile.publicPreview', { defaultValue: 'Public preview' })}
          </Typography>
          <Box sx={{ bgcolor: '#fff', border: '1px solid #e2ebe4', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ height: 130, bgcolor: '#dfe3e8' }} />
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 800 }}>{profile.businessName}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {[profile.businessType].filter(Boolean).join(' · ')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip label={t('profile.badgePriced', { defaultValue: 'Priced primary' })} size="small" backgroundColor="#FFF1E3" textColor="#C2410C" fontSize={11} />
                {profile.snapAuthorized && <Chip label={t('profile.badgeSnap', { defaultValue: 'SNAP/EBT' })} size="small" backgroundColor="#E3F4E7" textColor="#1B7A33" fontSize={11} />}
                <Chip label={t('profile.badgeOpen', { defaultValue: 'Open now' })} size="small" backgroundColor="#E3F4E7" textColor="#1B7A33" fontSize={11} />
              </Box>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('profile.previewHelp', { defaultValue: 'How shoppers see you in the store finder.' })}
          </Typography>
        </GLPGrid>
      </GLPGrid>
    </Box>
  );
}
